import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './utils';

/**
 * PillButton — full-rounded action button.
 *
 * Grammar: a single button shape (pill) with variant tints. The reference
 * design system splits filled (12px emphasis3) from outlined (15px +
 * line-height trick for height); we instead use explicit `h-*` per size,
 * keeping uniform typography (12px / 600) across all variants — a row of
 * mixed buttons shares one rhythm.
 *
 * Variant grammar — no visible borders, transparency only:
 *   primary: neutral-inverse fill — bg-foreground / text-background
 *            (maximum-contrast commit action; dark-on-light in light mode,
 *            light-on-dark in dark; brand accent is reserved for links and
 *            focus, not buttons)
 *   ghost:   low tint   — foreground @ 5%  (hover 10%)
 *   outline: mid tint   — foreground @ 10% (hover 20%) — replaces hairline
 *            border with transparency; clean scale solid → mid → low
 *   danger:  destructive solid
 *
 * Sizes (design-system tokens — PR-DS-A):
 *   sm → h-btn-sm (32px) + px-card-sm (16px)   compact, filters / chip-like
 *   md → h-btn-md (40px) + px-card-sm (16px)   default action-bar height
 *   lg → h-btn-lg (44px) + px-card    (24px)   hero / auth screens
 *
 * Icon convention: lucide icons inside PillButton are always `h-4 w-4` (16px)
 * regardless of button size — uniform icon-in-button rhythm
 * (16px icon + 8px gap + 12px/600 label).
 *
 * Tokens, not built-ins: this primitive consumes the semantic tokens
 * (`h-btn-*`, `px-card*`, `gap-inline`, `text-emphasis3`) directly. They used
 * to be silently stripped by tailwind-merge inside `cn()` and were temporarily
 * replaced by raw built-ins (`h-8/px-4/text-xs font-semibold`). PR-DS-A taught
 * twMerge our token class groups (see `utils.ts`), so the design system is the
 * single source of truth for size + rhythm. Token ↔ built-in mapping is 1:1
 * (no visual diff): h-btn-sm/md/lg = 32/40/44px, px-card-sm = 16px,
 * px-card = 24px, gap-inline = 8px, text-emphasis3 = 12px/600.
 */

const pillButtonVariants = cva(
  'inline-flex flex-none items-center justify-center gap-inline whitespace-nowrap rounded-full text-emphasis3 ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-foreground text-background hover:bg-foreground/90',
        ghost:
          'bg-foreground/5 text-foreground hover:bg-foreground/10',
        outline:
          'bg-foreground/10 text-foreground hover:bg-foreground/20',
        danger:
          'bg-state-danger text-state-danger-foreground hover:bg-state-danger/90',
      },
      size: {
        sm: 'h-btn-sm px-card-sm',
        md: 'h-btn-md px-card-sm',
        lg: 'h-btn-lg px-card',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface PillButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof pillButtonVariants> {
  asChild?: boolean;
}

export const PillButton = React.forwardRef<HTMLButtonElement, PillButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(pillButtonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
PillButton.displayName = 'PillButton';

export { pillButtonVariants };
